import { StreamChat } from 'stream-chat';
import { authFetch } from '../utils/authFetch';

class StreamChatService {
  constructor() {
    this.client = null;
    this.currentUser = null;
    this.currentUserId = null; // Store user ID separately
    this.channels = new Map();
    this.unsubscribeFunctions = [];
    this.userImageCache = {}; // Cache for user profile images
    this.userMetaCache = {}; // Cache for user metadata (e.g. handle) keyed by Stream user id
    this.ensureUserMetaInflight = new Map(); // Single-flight guard for ensure-user calls
  }

  async ensureUserMeta(streamUserId) {
    if (!streamUserId) return null;

    // Cache hit (including negative cache where handle is null)
    if (Object.prototype.hasOwnProperty.call(this.userMetaCache, streamUserId)) {
      return this.userMetaCache[streamUserId];
    }

    // Single-flight
    if (this.ensureUserMetaInflight.has(streamUserId)) {
      return this.ensureUserMetaInflight.get(streamUserId);
    }

    const p = (async () => {
      try {
        const ensured = await this.ensureUserExists(streamUserId);
        const handle =
          ensured?.userData?.handle ||
          ensured?.userData?.custom?.handle ||
          null;
        const meta = { handle };
        this.userMetaCache[streamUserId] = meta;
        return meta;
      } catch (e) {
        // Negative cache to avoid hammering
        const meta = { handle: null };
        this.userMetaCache[streamUserId] = meta;
        return meta;
      } finally {
        this.ensureUserMetaInflight.delete(streamUserId);
      }
    })();

    this.ensureUserMetaInflight.set(streamUserId, p);
    return p;
  }

  async initialize(userId, userToken, userData) {
    try {
      // Get API key from environment or use a placeholder for development
      const apiKey = process.env.REACT_APP_STREAM_API_KEY || 'your-api-key';
      
      if (!apiKey || apiKey === 'your-api-key') {
        console.warn('Stream API key not configured. Using mock mode.');
        return null;
      }

      this.client = StreamChat.getInstance(apiKey);
      
      // Store the user ID for later use
      this.currentUserId = userId;
      
      // Connect user to Stream
      this.currentUser = await this.client.connectUser(
        {
          id: userId,
          name: userData.name,
          image: userData.avatar || userData.image,
          email: userData.email,
          role: userData.activePersona || userData.userType || userData.role
        },
        userToken
      );

      // Set up global event listeners
      this.setupGlobalListeners();

      return this.client;
    } catch (error) {
      console.error('Failed to initialize Stream Chat:', error);
      throw error;
    }
  }

  /**
   * Returns a privacy-safe display name for a user.
   * - If the name looks like a person "First Last" (exactly 2 alpha tokens), show only the first token.
   * - Otherwise, return the provided name as-is.
   * - If no name, fall back to handle, then first name fields, then id.
   */
  getSafeDisplayName(user) {
    try {
      if (!user) return 'User';
      const rawName = (user.name || '').trim();
      if (rawName) {
        const tokens = rawName.split(/\s+/);
        const isTwoWordHumanName =
          tokens.length === 2 &&
          /^[A-Za-z][A-Za-z'.-]*$/.test(tokens[0]) &&
          /^[A-Za-z][A-Za-z'.-]*$/.test(tokens[1]);
        if (isTwoWordHumanName) {
          return tokens[0];
        }
        return rawName;
      }
      const handle = user.handle || user.username || user.custom?.handle;
      if (handle) return `@${String(handle).replace(/^@/, '')}`;
      const first =
        user.first_name || user.firstName || user.custom?.firstName;
      if (first) return String(first);
      return user.id || 'User';
    } catch {
      return 'User';
    }
  }

  setupGlobalListeners() {
    if (!this.client) return;

    // Listen for new messages across all channels
    const handleNewMessage = this.client.on('message.new', (event) => {
      console.log('New message received:', event.message);
      // Could trigger notifications here
    });

    // Listen for typing events
    const handleTyping = this.client.on('typing.start', (event) => {
      console.log('User typing:', event.user);
    });

    // Listen for user presence changes
    const handleUserPresence = this.client.on('user.presence.changed', (event) => {
      console.log('User presence changed:', event.user);
    });

    this.unsubscribeFunctions.push(
      handleNewMessage.unsubscribe,
      handleTyping.unsubscribe,
      handleUserPresence.unsubscribe
    );
  }

  async queryChannels(filters = {}, sort = {}, options = {}) {
    if (!this.client) {
      console.warn('Stream client not initialized');
      return [];
    }

    try {
      // Default filter: channels where current user is a member
      // Use the stored user ID string
      const defaultFilter = { 
        type: 'messaging',
        members: { $in: [this.currentUserId] }
      };

      const channelFilter = { ...defaultFilter, ...filters };
      // Sort by updated_at first (includes newly created empty channels),
      // then by last_message_at as secondary. This ensures new DMs without
      // messages are not excluded from the list.
      const channelSort = { updated_at: -1, last_message_at: -1, ...sort };
      const channelOptions = { 
        watch: true, 
        state: true, 
        limit: 50, // Increased from 30 to reduce likelihood of missing new channels
        ...options 
      };

      const channels = await this.client.queryChannels(
        channelFilter,
        channelSort,
        channelOptions
      );

      // Cache channels
      channels.forEach(channel => {
        this.channels.set(channel.id, channel);
      });

      return channels;
    } catch (error) {
      console.error('Failed to query channels:', error);
      return [];
    }
  }

  async ensureUserExists(targetUserId, authToken = null) {
    try {
      // Extract just the UUID part if the targetUserId already has a role prefix
      // Handles both dash-prefixed (creator-uuid) and underscore-prefixed (creator_uuid) formats
      let userId = targetUserId;
      
      // Known role prefixes that need to be stripped
      const rolePrefixPattern = /^(creator|host|traveler|admin|user)[-_]/i;
      const match = targetUserId.match(rolePrefixPattern);
      if (match) {
        // Remove the prefix (e.g., "creator-" or "host_") to get the bare UUID
        userId = targetUserId.slice(match[0].length);
      }

      const response = await authFetch('/api/messaging/ensure-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ targetUserId: userId })
      }, { disconnectChat: () => this.disconnect() });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to ensure user exists');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to ensure user exists:', error);
      throw error;
    }
  }

  async createDirectChannel(targetUserId, targetUserData = {}, authToken = null) {
    if (!this.client) {
      console.warn('Stream client not initialized');
      return null;
    }

    try {
      // Always ensure the target user exists in Stream Chat
      let actualTargetUserId = targetUserId;
      let actualTargetUserData = targetUserData;

      // Try to ensure user exists - this will handle both role-prefixed and bare UUIDs
      // The API endpoint will check if the user exists in database and create/update them in Stream
      try {
        console.log('Ensuring user exists in Stream:', targetUserId);
        const ensureResult = await this.ensureUserExists(targetUserId, authToken);
        actualTargetUserId = ensureResult.streamUserId;
        actualTargetUserData = { ...targetUserData, ...ensureResult.userData };
        console.log('User ensured in Stream:', actualTargetUserId);
      } catch (error) {
        console.warn('Could not ensure user exists, proceeding with original ID:', error.message);
        // If ensure fails, proceed with the original ID - it might already exist in Stream
        // This is a fallback for backwards compatibility
      }

      // Create a unique channel ID for 1-on-1 conversations
      const memberIds = [this.currentUserId, actualTargetUserId].sort();
      
      // Create a shorter channel ID to stay under Stream's 64 character limit
      // Extract just the UUID parts (removing the role prefix)
      const id1 = memberIds[0].split('-').slice(1).join('-');
      const id2 = memberIds[1].split('-').slice(1).join('-');
      
      // Use first 8 chars of each UUID for a unique but short ID
      const channelId = `dm-${id1.substring(0, 8)}-${id2.substring(0, 8)}`;

      // First, try to get the channel if it already exists
      let channel = this.channels.get(channelId);
      
      if (!channel) {
        // Try to query if the channel exists
        try {
          const existingChannels = await this.client.queryChannels({
            type: 'messaging',
            id: channelId,
            members: { $in: [this.currentUserId] }
          });
          
          if (existingChannels.length > 0) {
            channel = existingChannels[0];
            this.channels.set(channelId, channel);
            await channel.watch();
            return channel;
          }
        } catch (error) {
          console.log('Could not query for existing channel, will try to create:', error.message);
        }
      }

      // Channel doesn't exist, create it
      // Try client-side first (works for hosts/admin users)
      channel = this.client.channel('messaging', channelId, {
        members: memberIds,
        name: actualTargetUserData.name || actualTargetUserData.title || targetUserData.name || targetUserData.title || `Chat with ${actualTargetUserId}`,
        created_by_id: this.currentUserId
      });

      try {
        await channel.watch();
      } catch (error) {
        // If watch fails due to permission error (e.g., creators can't create channels),
        // create the channel server-side instead
        const errorMessage = error.message || error.toString() || '';
        const isPermissionError = errorMessage.includes('not allowed to perform action') ||
                                  errorMessage.includes('RecreateChannel') ||
                                  errorMessage.includes('permission') ||
                                  (error.code && error.code === 17); // Stream error code 17 is permission denied
        
        if (isPermissionError) {
          console.log('Channel creation requires server-side permissions, creating via API...');
          
          const channelName = actualTargetUserData.name || actualTargetUserData.title || targetUserData.name || targetUserData.title || `Chat with ${actualTargetUserId}`;

          // Create channel server-side (has admin privileges) using authFetch
          const response = await authFetch('/api/messaging/create-channel', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              targetUserId: actualTargetUserId,
              channelId,
              channelName
            })
          }, { disconnectChat: () => this.disconnect() });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Failed to create channel' }));
            throw new Error(errorData.error || 'Failed to create channel');
          }

          // After server-side creation, get the channel client-side
          channel = this.client.channel('messaging', channelId);
          await channel.watch();
        } else {
          // Re-throw if it's a different error
          throw error;
        }
      }

      // Cache the channel
      this.channels.set(channelId, channel);

      return channel;
    } catch (error) {
      console.error('Failed to create direct channel:', error);
      throw error;
    }
  }

  async getChannel(channelId) {
    // Check cache first
    if (this.channels.has(channelId)) {
      return this.channels.get(channelId);
    }

    if (!this.client) {
      console.warn('Stream client not initialized');
      return null;
    }

    try {
      const channel = this.client.channel('messaging', channelId);
      await channel.watch();
      
      // Cache the channel
      this.channels.set(channelId, channel);
      
      return channel;
    } catch (error) {
      console.error('Failed to get channel:', error);
      return null;
    }
  }

  async uploadFile(channelId, file) {
    const channel = await this.getChannel(channelId);
    if (!channel) {
      throw new Error('Channel not found');
    }

    try {
      // Upload file to Stream Chat's CDN
      const response = await channel.sendFile(file);
      return response.file; // Returns the URL of the uploaded file
    } catch (error) {
      console.error('Failed to upload file:', error);
      throw error;
    }
  }

  async sendMessage(channelId, messageData) {
    const channel = await this.getChannel(channelId);
    if (!channel) {
      throw new Error('Channel not found');
    }

    try {
      // Process attachments: upload files and format for Stream Chat
      let formattedAttachments = [];
      
      if (messageData.attachments && messageData.attachments.length > 0) {
        // Upload each file and format the attachment
        for (const attachment of messageData.attachments) {
          // If attachment has a File object, upload it first
          if (attachment.file instanceof File) {
            try {
              const fileUrl = await this.uploadFile(channelId, attachment.file);
              formattedAttachments.push({
                type: attachment.type === 'image' ? 'image' : 'file',
                asset_url: fileUrl,
                title: attachment.name || attachment.file.name,
                file_size: attachment.size || attachment.file.size,
                mime_type: attachment.file.type
              });
            } catch (uploadError) {
              console.error('Failed to upload attachment:', uploadError);
              // Continue with other attachments even if one fails
            }
          } 
          // If attachment already has a URL (from previous upload), format it
          else if (attachment.url && attachment.url.startsWith('http')) {
            formattedAttachments.push({
              type: attachment.type === 'image' ? 'image' : 'file',
              asset_url: attachment.url,
              title: attachment.name,
              file_size: attachment.size,
              mime_type: attachment.mimeType || attachment.type
            });
          }
          // If attachment is already in Stream Chat format, use it as-is
          else if (attachment.asset_url) {
            formattedAttachments.push(attachment);
          }
        }
      }

      // Build the message payload
      const messagePayload = {
        text: messageData.text,
        attachments: formattedAttachments.length > 0 ? formattedAttachments : undefined,
        mentioned_users: messageData.mentionedUsers,
        parent_id: messageData.replyTo
      };

      // Add custom fields for collab requests (these are stored on the message)
      if (messageData.collabRequestId) {
        messagePayload.collabRequestId = messageData.collabRequestId;
      }
      if (messageData.collabRequestSnapshot) {
        messagePayload.collabRequestSnapshot = messageData.collabRequestSnapshot;
      }
      if (messageData.collabRequestRecipientId) {
        messagePayload.collabRequestRecipientId = messageData.collabRequestRecipientId;
      }
      if (messageData.collabTargetKind) {
        messagePayload.collabTargetKind = messageData.collabTargetKind;
      }
      if (messageData.collabTargetId) {
        messagePayload.collabTargetId = messageData.collabTargetId;
      }
      // Flag for decline response messages (to suppress legacy decline CTA)
      if (messageData.collabDeclineResponse) {
        messagePayload.collabDeclineResponse = messageData.collabDeclineResponse;
      }
      // Flag for accept response messages (to suppress legacy decline CTA)
      if (messageData.collabAcceptResponse) {
        messagePayload.collabAcceptResponse = messageData.collabAcceptResponse;
      }

      const message = await channel.sendMessage(messagePayload);

      return message;
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }
  }

  async markAsRead(channelId) {
    const channel = await this.getChannel(channelId);
    if (!channel) return;

    try {
      await channel.markRead();
    } catch (error) {
      console.error('Failed to mark channel as read:', error);
    }
  }

  async sendTypingEvent(channelId) {
    const channel = await this.getChannel(channelId);
    if (!channel) return;

    try {
      await channel.keystroke();
    } catch (error) {
      console.error('Failed to send typing event:', error);
    }
  }

  async stopTypingEvent(channelId) {
    const channel = await this.getChannel(channelId);
    if (!channel) return;

    try {
      await channel.stopTyping();
    } catch (error) {
      console.error('Failed to stop typing event:', error);
    }
  }

  async deleteMessage(messageId, channelId) {
    if (!this.client) return;

    try {
      await this.client.deleteMessage(messageId);
    } catch (error) {
      console.error('Failed to delete message:', error);
      throw error;
    }
  }

  async updateMessage(messageId, messageData) {
    if (!this.client) return;

    try {
      const updatedMessage = await this.client.updateMessage({
        id: messageId,
        text: messageData.text,
        attachments: messageData.attachments
      });
      return updatedMessage;
    } catch (error) {
      console.error('Failed to update message:', error);
      throw error;
    }
  }

  async deleteChannel(channelId, authToken = null) {
    if (!this.client || !channelId) return;

    try {
      const channel = this.client.channel('messaging', channelId);
      
      // Try to delete the channel client-side first
      try {
        await channel.delete();
        return true;
      } catch (error) {
        // If delete fails due to permission error, use server-side deletion
        const errorMessage = error.message || error.toString() || '';
        const isPermissionError = errorMessage.includes('not allowed to perform action') ||
                                  errorMessage.includes('DeleteChannel') ||
                                  errorMessage.includes('permission') ||
                                  (error.code && error.code === 17); // Stream error code 17 is permission denied
        
        if (isPermissionError) {
          console.log('Channel deletion requires server-side permissions, deleting via API...');

          // Delete channel server-side (has admin privileges) using authFetch
          const response = await authFetch('/api/messaging/delete-channel', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ channelId })
          }, { disconnectChat: () => this.disconnect() });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Failed to delete channel' }));
            throw new Error(errorData.error || 'Failed to delete channel');
          }

          // Remove from cache if deletion was successful
          this.channels.delete(channelId);
          
          return true;
        } else {
          // Re-throw if it's a different error
          throw error;
        }
      }
    } catch (error) {
      console.error('Failed to delete channel:', error);
      throw error;
    }
  }

  async fetchUserImages(userIds, timeoutMs = 5000) {
    if (!userIds || userIds.length === 0) return {};
    
    // Check cache first
    const uncachedIds = userIds.filter(id => !this.userImageCache[id]);
    
    if (uncachedIds.length === 0) {
      // All images are cached
      const result = {};
      userIds.forEach(id => {
        result[id] = this.userImageCache[id];
      });
      return result;
    }
    
    // Helper to build fallback result with cached or default images
    const buildFallbackResult = () => {
      const result = {};
      userIds.forEach(id => {
        result[id] = this.userImageCache[id] || {
          url: `https://ui-avatars.com/api/?name=User&background=random&size=200`,
          name: 'User'
        };
      });
      return result;
    };
    
    try {
      // Use AbortController with timeout to prevent slow requests from blocking UI
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      
      // Fetch uncached images from the database
      const response = await fetch('/api/messaging/user-images', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userIds: uncachedIds }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        if (data.ok && data.images) {
          // Update cache
          Object.assign(this.userImageCache, data.images);
          
          // Return all requested images
          const result = {};
          userIds.forEach(id => {
            result[id] = this.userImageCache[id] || {
              url: `https://ui-avatars.com/api/?name=User&background=random&size=200`,
              name: 'User'
            };
          });
          return result;
        }
      }
    } catch (error) {
      // Check if this was a timeout abort
      const isAbort = error && (error.name === 'AbortError' || error.code === 'ABORT_ERR');
      if (isAbort) {
        console.warn('fetchUserImages timed out after', timeoutMs, 'ms, using fallback avatars');
      } else {
        console.error('Failed to fetch user images:', error);
      }
    }
    
    // Return default images on error or timeout
    return buildFallbackResult();
  }

  async searchUsers(searchTerm, filters = {}, options = {}) {
    if (!this.client) return [];

    try {
      // Don't return any users if search is empty
      if (!searchTerm || !searchTerm.trim()) {
        return [];
      }
      
      const search = searchTerm.trim();
      
      // First, try searching our database for users (includes handle search)
      try {
        console.log('[streamChat.searchUsers] Searching database for:', search);
        const controller = new AbortController();
        // Increase timeout for production (20 seconds)
        const timeoutDuration = 20000;
        const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);
        let url = `/api/messaging/search-users?query=${encodeURIComponent(search)}`;
        if (options.includeProperties) {
          url += '&includeProperties=true';
        }
        const response = await fetch(url, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        console.log('[streamChat.searchUsers] Response status:', response.status);
        console.log('[streamChat.searchUsers] Response ok:', response.ok);
        
        if (!response.ok) {
          console.error('[streamChat.searchUsers] Response not OK:', response.status, response.statusText);
          const text = await response.text().catch(() => '');
          console.error('[streamChat.searchUsers] Response body:', text);
        } else {
          const data = await response.json();
          console.log('[streamChat.searchUsers] Response data:', data);
          
          if (data.ok && data.users && data.users.length > 0) {
            console.log('[streamChat.searchUsers] Returning', data.users.length, 'users from database');
            return data.users.map(u => {
              if (u.resultType === 'property') return u;
              return { ...u, name: this.getSafeDisplayName(u) };
            });
          } else {
            console.log('[streamChat.searchUsers] No users found in database response');
          }
        }
      } catch (err) {
        const isAbort = err && (err.name === 'AbortError' || err.code === 'ABORT_ERR');
        console.error('Database search failed, falling back to Stream:', isAbort ? 'timeout' : err);
      }
      
      // Fallback to Stream search if database search fails or returns no results
      const cleanSearch = search.toLowerCase().startsWith('@') ? search.substring(1) : search.toLowerCase();
      
      // Search by name with autocomplete (fuzzy search)
      const queryFilter = { name: { $autocomplete: cleanSearch } };
      
      const { users } = await this.client.queryUsers(
        { ...queryFilter, ...filters },
        { name: 1 },
        { limit: 30 } // Get more results to filter through
      );
      
      // Use the users from the name search directly
      // (The problematic ID search is not supported by Stream and was causing errors)
      let allUsers = [...users];
      
      // Filter and sort results
      const processedUsers = allUsers
        .filter(user => {
          const userName = (user.name || '').toLowerCase();
          const userId = (user.id || '').toLowerCase();
          const userHandle = (user.handle || user.username || '').toLowerCase();
          
          // Check if search matches name, handle, or part of ID
          return userName.includes(cleanSearch) || 
                 userId.includes(cleanSearch) ||
                 userHandle.includes(cleanSearch);
        })
        .map(user => {
          // Extract the user type from custom field or from the ID prefix
          let userType = 'User';
          
          // First check custom.userType field
          if (user.custom?.userType) {
            userType = user.custom.userType.charAt(0).toUpperCase() + user.custom.userType.slice(1);
          } 
          // Fallback: extract from ID if it has the format "host-uuid" or "creator-uuid"
          else if (user.id) {
            if (user.id.startsWith('host-')) {
              userType = 'Host';
            } else if (user.id.startsWith('creator-')) {
              userType = 'Creator';
            }
          }
          
          // Extract handle and first_name from Stream user object
          // Stream stores these in custom fields or top-level
          const handle = user.handle || user.username || user.custom?.handle || null;
          const firstName = user.first_name || user.firstName || user.custom?.firstName || 
                           (user.name ? user.name.split(' ')[0] : null);
          
          return {
            ...user,
            role: userType,
            handle: handle,
            first_name: firstName,
            // Ensure image is set
            image: user.image || user.avatar || user.avatar_url || null
          };
        })
        .sort((a, b) => {
          // Prioritize exact matches
          const aName = (a.name || '').toLowerCase();
          const bName = (b.name || '').toLowerCase();
          const aHandle = (a.handle || a.username || '').toLowerCase();
          const bHandle = (b.handle || b.username || '').toLowerCase();
          
          const aExactName = aName.startsWith(cleanSearch);
          const bExactName = bName.startsWith(cleanSearch);
          const aExactHandle = aHandle.startsWith(cleanSearch);
          const bExactHandle = bHandle.startsWith(cleanSearch);
          
          if ((aExactName || aExactHandle) && !(bExactName || bExactHandle)) return -1;
          if (!(aExactName || aExactHandle) && (bExactName || bExactHandle)) return 1;
          
          return aName.localeCompare(bName);
        })
        .slice(0, 10); // Return top 10 results

      return processedUsers.map(u => ({ ...u, name: this.getSafeDisplayName(u) }));
    } catch (error) {
      console.error('Failed to search users:', error);
      return [];
    }
  }

  disconnect() {
    // Unsubscribe from all listeners
    this.unsubscribeFunctions.forEach(unsubscribe => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    });
    this.unsubscribeFunctions = [];

    // Clear channel cache
    this.channels.clear();

    // Disconnect from Stream
    if (this.client) {
      this.client.disconnectUser();
      this.client = null;
      this.currentUser = null;
      this.currentUserId = null;
    }
  }

  // Helper method to format Stream data to match our UI structure
  async formatChannelForUI(channel) {
    console.log('[formatChannelForUI] Processing channel:', channel.id);
    console.log('[formatChannelForUI] Channel members:', channel.state.members);
    console.log('[formatChannelForUI] Current user ID:', this.currentUserId);
    
    const otherMembers = Object.values(channel.state.members).filter(
      member => member.user.id !== this.currentUserId
    );
    
    console.log('[formatChannelForUI] Other members:', otherMembers);

    // Fetch profile images for other members
    const otherMemberIds = otherMembers.map(m => m.user.id);
    const userImages = await this.fetchUserImages(otherMemberIds);

    const lastMessage = channel.state.messages[channel.state.messages.length - 1];
    const unreadCount = channel.state.unreadCount || 0;

    const priorityName = (user) => {
      console.log('[priorityName] User object:', user);
      
      // Check if this is a creator (for privacy, only show first name)
      const isCreator = user.role === 'creator' || user.userType === 'creator' || 
                        user.custom?.userType === 'creator' || 
                        user.extra?.userType === 'creator';
      
      // For creators, prioritize first_name over name (privacy)
      if (isCreator) {
        const first = user.first_name || user.firstName || user.custom?.firstName;
        if (first) {
          console.log('[priorityName] Creator - Returning first name:', first);
          return first;
        }
        
        // Fallback to handle for creators
        const handle = user.handle || user.username || user.extra?.handle || user.custom?.handle;
        if (handle) {
          console.log('[priorityName] Creator - Returning handle:', handle);
          return `@${handle.replace(/^@/, '')}`;
        }
      }
      
      // For non-creators or if creator doesn't have first_name, use name field (sanitized to first-name-only for human names)
      if (user.name && user.name.trim() !== '') {
        const safe = this.getSafeDisplayName({ name: user.name });
        console.log('[priorityName] Returning sanitized name:', safe);
        return safe;
      }
      
      // Second priority: handle
      const handle = user.handle || user.username || user.extra?.handle || user.custom?.handle;
      if (handle) {
        console.log('[priorityName] Returning handle:', handle);
        return `@${handle.replace(/^@/, '')}`;
      }
      
      // Third priority: first name
      const first = user.first_name || user.firstName || user.custom?.firstName;
      if (first) {
        console.log('[priorityName] Returning first name:', first);
        return first;
      }
      
      // Fourth priority: brand/property name
      const property = user.brand_name || user.property_name || user.extra?.brand_name || user.extra?.property_name;
      if (property) {
        console.log('[priorityName] Returning property name:', property);
        return property;
      }
      
      // Last resort: user ID
      console.log('[priorityName] Falling back to ID:', user.id);
      return user.id;
    };

    // For direct messages (1:1), do NOT use the shared channel name because it's not symmetric and
    // can be a context/title string (e.g. property/brand), which causes users to see their own brand/name.
    // Instead, always derive the display name from the "other member" so both sides see the counterpart.
    const rawChannelProvidedName = channel.data.name ? this.getSafeDisplayName({ name: channel.data.name }) : null;
    const isDirectMessage = otherMembers.length === 1;
    const channelProvidedName = isDirectMessage ? null : rawChannelProvidedName;
    const computedName = channelProvidedName || otherMembers.map(m => priorityName(m.user)).join(', ');

    // Get the profile image for the first other member (for 1-on-1 chats)
    const primaryMemberId = otherMembers[0]?.user.id;
    const primaryImage = primaryMemberId && userImages[primaryMemberId] 
      ? userImages[primaryMemberId].url 
      : otherMembers[0]?.user.image;

    // Extract handle and first_name from primary member (for 1-on-1 chats)
    const primaryMember = otherMembers[0]?.user;
    const primaryHandle = primaryMember ? (
      primaryMember.handle || 
      primaryMember.username || 
      primaryMember.extra?.handle || 
      primaryMember.custom?.handle
    ) : null;
    const primaryFirstName = primaryMember ? (
      primaryMember.first_name || 
      primaryMember.firstName || 
      primaryMember.custom?.firstName
    ) : null;

    // Backfill missing handle for DMs by ensuring the other user exists in Stream with handle populated.
    // This prevents handle disappearing on refresh when Stream member data lacks custom.handle.
    let resolvedPrimaryHandle = primaryHandle;
    if (otherMembers.length === 1 && primaryMember && !resolvedPrimaryHandle) {
      const meta = await this.ensureUserMeta(primaryMember.id);
      if (meta?.handle) {
        resolvedPrimaryHandle = meta.handle;
      }
    }

    return {
      id: channel.id,
      name: computedName,
      avatar: primaryImage,
      // Add handle and first_name for 1-on-1 conversations (not groups)
      handle: otherMembers.length === 1 ? resolvedPrimaryHandle : null,
      first_name: otherMembers.length === 1 ? primaryFirstName : null,
      avatars: otherMembers.map(m => {
        const img = userImages[m.user.id];
        return img ? img.url : m.user.image;
      }),
      lastMessage: lastMessage?.text || 'No messages yet',
      timestamp: lastMessage?.created_at || channel.data.created_at,
      unreadCount,
      isOnline: otherMembers.some(m => m.user.online),
      isGroup: otherMembers.length > 1,
      memberCount: channel.state.members.length,
      members: Object.values(channel.state.members).map(m => {
        const img = userImages[m.user.id];
        return {
          ...m.user,
          image: img ? img.url : m.user.image
        };
      }),
      isTyping: Object.values(channel.state.typing).some(
        user => user.id !== this.currentUserId
      )
    };
  }

  formatMessageForUI(message) {
    const streamUserId = message.user?.id;
    const cached = streamUserId ? this.userImageCache[streamUserId] : null;
    const resolvedAvatar =
      (cached && cached.url) ||
      message.user?.image ||
      message.user?.avatar ||
      message.user?.avatar_url ||
      '/images/content/avatar-1.jpg';
    
    // Normalize Stream Chat attachments to match UI expectations
    // Stream Chat uses asset_url and title, UI expects url and name
    const normalizedAttachments = message.attachments?.map(attachment => ({
      ...attachment,
      url: attachment.asset_url || attachment.url || attachment.image_url,
      name: attachment.title || attachment.name || 'Attachment',
      type: attachment.type || (attachment.image_url ? 'image' : 'file')
    })) || [];
    
    return {
      id: message.id,
      text: message.text,
      timestamp: message.created_at,
      sender: {
        id: message.user?.id,
        name: this.getSafeDisplayName(message.user),
        avatar: resolvedAvatar
      },
      isDelivered: true,
      isRead: message.readBy?.length > 1,
      isEdited: message.updated_at !== message.created_at,
      attachments: normalizedAttachments,
      replyTo: message.parent_id,
      isSystem: message.type === 'system',
      // Collaboration request fields (preserved from Stream message)
      collabRequestId: message.collabRequestId || null,
      collabRequestSnapshot: message.collabRequestSnapshot || null,
      collabRequestRecipientId: message.collabRequestRecipientId || null,
      collabTargetKind: message.collabTargetKind || null,
      collabTargetId: message.collabTargetId || null,
      isCollabRequest: !!message.collabRequestId,
      // Decline response flag (to suppress legacy decline CTA on the other end)
      collabDeclineResponse: message.collabDeclineResponse || false,
      // Accept response flag (to suppress legacy decline CTA on the other end)
      collabAcceptResponse: message.collabAcceptResponse || false,
    };
  }
}

// Create singleton instance
const streamChatService = new StreamChatService();

export default streamChatService;
